import { ComponentCoordinate, HgsArticle } from 'api3';
import { CommonModule } from '@angular/common';
import { Component, ViewEncapsulation, computed, input, signal } from '@angular/core';
import { HgsSvgWiringDiagramComponent } from './components/hgs-svg-wiring-diagram.component';
import { TranslocoPipe } from '@jsverse/transloco';
import { MarkdownModule } from 'ngx-markdown';

// TODO: will be replaced with ticket
// https://linear.app/ahead-automotive/issue/QI-1523/be-follow-up-hgs-api
const DUMMY_SYSTEM = {
  systemId: '',
  systemName: {
    language: 'de',
    title: '',
  },
  dateTo: '',
  imagePlacementUrl: '',
  components: [],
};

export type SystemComponent = {
  id: string;
  componentId: string;
  genericArticleNos?: number[];
  genericArticleNumbers?: number[];
  group?: { language: string; title: string };
  name?: { language: string; title: string };
  text?: { language: string; title: string };
  placementText?: { language: string; title: string };
  shortName?: string;
  showInComponentList: boolean;
  type?: string;
  xAbs: string;
  yAbs: string;
};

export type SystemImage = {
  components?: SystemComponent[];
  imagePlacementUrl?: string;
  systemId: string;
  systemName?: { language: string; title: string };
};

@Component({
  selector: 'app-hgs-wiring-diagram',
  template: `
    @if (data() && data()?.text) {
      <div class="flex h-[88vh] flex-col gap-4">
        <div>
          <h2 class="mb-2 w-full overflow-hidden text-ellipsis whitespace-nowrap">{{ image()['systemName']['title'] }}</h2>
          <app-hgs-svg-wiring-diagram [system]="image()" [shownBox]="componentToShow()" class="block w-full" imgClass="" />
        </div>

        <div class="flex-1 overflow-hidden border border-gray-300">
          <div class="h-full overflow-y-auto">
            <table class="w-full">
              <thead class="sticky top-0 z-10 bg-gray-50 drop-shadow">
                <tr>
                  <th class="border-b border-gray-300 px-1 py-1 text-left text-xs lg:px-4 lg:py-2 lg:text-base">
                    {{ 'documents.hgs.component' | transloco }}
                  </th>
                  <th class="border-b border-gray-300 px-1 py-1 text-left text-xs lg:px-4 lg:py-2 lg:text-base">
                    {{ 'documents.hgs.group' | transloco }}
                  </th>
                  <th class="border-b border-gray-300 px-1 py-1 text-left text-xs lg:px-4 lg:py-2 lg:text-base">
                    {{ 'documents.hgs.location' | transloco }}
                  </th>
                </tr>
              </thead>
              <tbody>
                @for (comp of components(); track uniqueId(comp) + $index) {
                  <tr [attr.class]="uniqueId(comp) === uniqueId(componentToShow()) ? 'bg-blue-50' : 'bg-white'">
                    <td class="border-b border-gray-200 px-1 py-1 text-xs lg:px-4 lg:py-2 lg:text-base">
                      {{ comp['component']?.['shortName'] }} - {{ comp['component']?.['text']?.['title'] }}
                    </td>
                    <td class="border-b border-gray-200 px-1 py-1 text-xs lg:px-4 lg:py-2 lg:text-base">
                      {{ comp['component']?.['group']?.['title'] }}
                    </td>
                    <td class="border-b border-gray-200 px-1 py-1 text-xs lg:px-4 lg:py-2 lg:text-base">
                      @if (hasSufficientCoordinates(comp)) {
                        <button
                          class="rounded-lg border border-slate-800 bg-blue-100 px-2 py-1 text-slate-800 hover:border-slate-900 hover:bg-blue-200 hover:text-slate-900 lg:py-2 lg:text-sm"
                          (click)="onClick(comp)">
                          {{ 'documents.hgs.showPosition' | transloco }}
                        </button>
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
      </div>
    } @else {
      <p class="mt-8 text-center text-sm text-gray-500">{{ 'global.noOEDataFound' | transloco }}</p>
    }
  `,
  imports: [CommonModule, TranslocoPipe, MarkdownModule, HgsSvgWiringDiagramComponent],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
})
export class HgsWiringDiagramComponent {
  data = input<HgsArticle | undefined>();
  image = computed(() => ({
    ...DUMMY_SYSTEM,
    imagePlacementUrl: this.data()?.data?.wiringDiagram?.imageUrl ?? undefined,
    systemName: { language: 'de', title: this.data()?.data?.wiringDiagram?.title ?? '' },
  }));
  components = computed(() => this.data()?.data?.wiringDiagram?.componentCoordinates || []);

  componentToShow = signal<ComponentCoordinate | undefined>(undefined);

  uniqueId(component: ComponentCoordinate | undefined): string {
    return component ? `${component.component.id}-${component['x1']}-${component['x2']}--${component['y1']}-${component['y2']}` : '';
  }

  onClick(component: ComponentCoordinate | undefined) {
    this.componentToShow.set(component);
  }

  hasSufficientCoordinates(component: ComponentCoordinate): boolean {
    return component.x1 !== undefined && component.x2 !== undefined && component.y1 !== undefined && component.y2 !== undefined;
  }
}
